import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    useSelector } from "react-redux";
import {
  getAuthSession,
  GETACCEMPDETAILS,
  GETQUICKITEMS,
  isRoleAllowed,
  SUCCESS,
  ROLE_CREATE,
  ROLE_UPDATE,
  ROLE_VIEW,
  RECPMGMT
} from "../../constants/constant";
import { snpAxios } from "../global/api";
import Loader from "../global/loader/Loader";
import { Autocomplete, TextField } from "../ui";
import utilities from "../../utilities/utilities";
import {
  getRecipeIngredientLabel,
  getRecipeIngredientQuantityInput,
  isRecipeIngredientQuantityAllowed,
  isRecipeStockRateCard,
  toRecipeIngredient,
} from "./receipeIngredient";
import "./ReceipeV2.css";

const emptyIngredient = () => ({
  stockId: "", conversionId: "", stockName: "", unitName: "", unitRestrictions: false,
  decimalSmRestrictions: false, quantity: "", wastagePercent: "0", optional: false, notes: "",
});
const emptyStep = (stepNo = 1) => ({ stepNo, title: "", instruction: "", durationMinutes: "0" });
const emptyForm = () => ({
  id: "", stockId: "", stockName: "", receipyById: "", quantityType: "Full", quantity: 1,
  preparationTimeMinutes: "0", preparationNotes: "", ingredients: [emptyIngredient()],
  preparationSteps: [emptyStep()], originalIngredientIds: [],
});
const values = (response, key = "value_2") => response?.data?.jcJson?.[key] || [];
const messageOf = (error, fallback) => error?.response?.data?.errorMessage || error?.message || fallback;

export default function ReceipeV2() {
  const canView = isRoleAllowed(RECPMGMT, ROLE_VIEW);
  const canCreate = isRoleAllowed(RECPMGMT, ROLE_CREATE);
  const canEdit = isRoleAllowed(RECPMGMT, ROLE_UPDATE);
  const api = useSelector((state) => state.setApiUrlReducer.MAINURL.INITIAL_BASE_URL)?.BACKEND_BASE_URL;
  const shop = useSelector((state) => state.setActiveShopHashReducer.activeShopHash);
  const [receipes, setReceipes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [foodQuantityTypes, setFoodQuantityTypes] = useState({ Full: 1, Half: 0.5, Quarter: 0.25, Other: 0 });
  const [form, setForm] = useState(emptyForm());
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [notice, setNotice] = useState({ text: "", error: false });
  const [dishOptions, setDishOptions] = useState([]);
  const [dishSearchTerm, setDishSearchTerm] = useState("");
  const [dishLoading, setDishLoading] = useState(false);
  const [ingredientOptions, setIngredientOptions] = useState({});
  const [ingredientSearchTerms, setIngredientSearchTerms] = useState({});
  const [ingredientLoading, setIngredientLoading] = useState({});
  const ingredientSearchTimers = useRef({});
  const ingredientSearchRequests = useRef({});
  const dishSearchTimer = useRef();
  const dishSearchRequest = useRef(0);

  const params = useMemo(() => ({ apiToken: getAuthSession().apiToken, shopHash: shop?.id }), [shop?.id]);
  const postLookup = useCallback((path, extra = {}) => snpAxios.post(`${api}${path}`, {
    jcJson: { ...params, ...extra },
  }), [api, params]);

  const loadReceipes = useCallback(async () => {
    const response = await snpAxios.get(`${api}receipe`, { params });
    if (response.data.status === SUCCESS) {
      setReceipes(values(response, "data"));
      setFoodQuantityTypes(response.data.jcJson.foodQuantityType || {});
    } else setReceipes([]);
  }, [api, params]);

  const loadPage = useCallback(async () => {
    if (!api || !shop?.id) return;
    setLoading(true);
    setNotice({ text: "", error: false });
    try {
      const employeeResponse = await postLookup(GETACCEMPDETAILS, { roles: "" });
      setEmployees(values(employeeResponse));
      await loadReceipes();
    } catch (error) {
      setNotice({ text: messageOf(error, "Receipes could not be loaded."), error: true });
    } finally {
      setLoading(false);
    }
  }, [api, loadReceipes, postLookup, shop?.id]);

  useEffect(() => { loadPage(); }, [loadPage]);
  useEffect(() => () => {
    clearTimeout(dishSearchTimer.current);
    Object.values(ingredientSearchTimers.current).forEach((timer) => clearTimeout(timer));
  }, []);

  const searchDishes = useCallback((searchText) => {
    const query = searchText.trim();
    clearTimeout(dishSearchTimer.current);
    const requestId = dishSearchRequest.current + 1;
    dishSearchRequest.current = requestId;
    if (query.length < 2) {
      setDishOptions([]);
      setDishLoading(false);
      return;
    }
    dishSearchTimer.current = setTimeout(async () => {
      setDishLoading(true);
      try {
        const encodedHash = utilities.encode(shop.id);
        const url = `${api}${GETQUICKITEMS}?s=${encodedHash}&q=${encodeURIComponent(query)}`
          + "&m=1&stockVisible=-1&holdStock=-1&includeConversionDetails=true";
        const response = await snpAxios.get(url);
        if (dishSearchRequest.current !== requestId) return;
        setDishOptions(response.data.status === SUCCESS ? values(response) : []);
      } catch (error) {
        if (dishSearchRequest.current === requestId) {
          setDishOptions([]);
          setNotice({ text: messageOf(error, "Menu dishes could not be searched."), error: true });
        }
      } finally {
        if (dishSearchRequest.current === requestId) setDishLoading(false);
      }
    }, 350);
  }, [api, shop?.id]);

  const searchIngredients = useCallback((index, searchText) => {
    const query = searchText.trim();
    clearTimeout(ingredientSearchTimers.current[index]);
    const requestId = (ingredientSearchRequests.current[index] || 0) + 1;
    ingredientSearchRequests.current[index] = requestId;
    if (query.length < 2) {
      setIngredientOptions((current) => ({ ...current, [index]: [] }));
      setIngredientLoading((current) => ({ ...current, [index]: false }));
      return;
    }
    ingredientSearchTimers.current[index] = setTimeout(async () => {
      setIngredientLoading((current) => ({ ...current, [index]: true }));
      try {
        const encodedHash = utilities.encode(shop.id);
        const url = `${api}${GETQUICKITEMS}?s=${encodedHash}&q=${encodeURIComponent(query)}`
          + "&m=1&stockVisible=-1&holdStock=1&includeConversionDetails=true";
        const response = await snpAxios.get(url);
        if (ingredientSearchRequests.current[index] !== requestId) return;
        const options = response.data.status === SUCCESS ? values(response).filter(isRecipeStockRateCard) : [];
        setIngredientOptions((current) => ({ ...current, [index]: options }));
      } catch (error) {
        if (ingredientSearchRequests.current[index] === requestId) {
          setIngredientOptions((current) => ({ ...current, [index]: [] }));
          setNotice({ text: messageOf(error, "Ingredients could not be searched."), error: true });
        }
      } finally {
        if (ingredientSearchRequests.current[index] === requestId) {
          setIngredientLoading((current) => ({ ...current, [index]: false }));
        }
      }
    }, 350);
  }, [api, shop?.id]);

  const openNew = () => {
    if (!canCreate) {
      setNotice({ text: "Ask the owner for permission to add a receipe.", error: true });
      return;
    }
    Object.values(ingredientSearchTimers.current).forEach((timer) => clearTimeout(timer));
    ingredientSearchTimers.current = {};
    ingredientSearchRequests.current = {};
    clearTimeout(dishSearchTimer.current);
    dishSearchRequest.current += 1;
    setForm(emptyForm());
    setDishOptions([]);
    setDishSearchTerm("");
    setIngredientOptions({});
    setIngredientSearchTerms({});
    setOpen(true);
  };

  const openEdit = async (id) => {
    setSaving(true);
    Object.values(ingredientSearchTimers.current).forEach((timer) => clearTimeout(timer));
    ingredientSearchTimers.current = {};
    ingredientSearchRequests.current = {};
    clearTimeout(dishSearchTimer.current);
    dishSearchRequest.current += 1;
    try {
      const response = await snpAxios.get(`${api}receipe/${id}`, { params });
      if (response.data.status !== SUCCESS) throw new Error("Receipe could not be opened.");
      const item = response.data.jcJson.data;
      const ingredients = (item.ingredients?.length ? item.ingredients : item.listItems || []).map((ingredient) => ({
        ...emptyIngredient(), ...ingredient, quantity: String(ingredient.quantity ?? ""),
        wastagePercent: String(ingredient.wastagePercent ?? 0),
      }));
      const steps = (item.preparationSteps || []).map((step, index) => ({
        ...emptyStep(index + 1), ...step, durationMinutes: String(step.durationMinutes ?? 0),
      }));
      setForm({
        ...emptyForm(), ...item, receipyById: item.receipeById,
        ingredients: ingredients.length ? ingredients : [emptyIngredient()],
        preparationSteps: steps.length ? steps : [emptyStep()],
        originalIngredientIds: ingredients.map((ingredient) => ingredient.storedStockId || ingredient.stockId),
      });
      setIngredientOptions(Object.fromEntries(ingredients.map((ingredient, index) => [index,
        ingredient.stockId ? [{ id: ingredient.stockId, ...ingredient }] : []])));
      setIngredientSearchTerms(Object.fromEntries(ingredients.map((ingredient, index) => [index,
        ingredient.stockName || ""])));
      const dish = item.stockId ? [{ id: item.stockId, stockName: item.stockName || "" }] : [];
      setDishOptions(dish);
      setDishSearchTerm(item.stockName || "");
      setOpen(true);
    } catch (error) {
      setNotice({ text: messageOf(error, "Receipe could not be opened."), error: true });
    } finally { setSaving(false); }
  };

  const setIngredient = (index, key, value) => setForm((current) => ({
    ...current, ingredients: current.ingredients.map((ingredient, row) => row === index ? { ...ingredient, [key]: value } : ingredient),
  }));
  const setIngredientValues = (index, valuesToSet) => setForm((current) => ({
    ...current, ingredients: current.ingredients.map((ingredient, row) => row === index
      ? { ...ingredient, ...valuesToSet } : ingredient),
  }));
  const removeIngredient = (index) => {
    setForm((current) => ({
      ...current, ingredients: current.ingredients.filter((_, row) => row !== index),
    }));
    Object.values(ingredientSearchTimers.current).forEach((timer) => clearTimeout(timer));
    ingredientSearchTimers.current = {};
    ingredientSearchRequests.current = {};
    setIngredientOptions({});
    setIngredientSearchTerms({});
  };
  const setStep = (index, key, value) => setForm((current) => ({
    ...current, preparationSteps: current.preparationSteps.map((step, row) => row === index ? { ...step, [key]: value } : step),
  }));
  const removeStep = (index) => setForm((current) => ({
    ...current, preparationSteps: current.preparationSteps.filter((_, row) => row !== index)
      .map((step, row) => ({ ...step, stepNo: row + 1 })),
  }));

  const validate = () => {
    if (!form.stockId) return "Choose the dish this receipe prepares.";
    if (!form.ingredients.length) return "Add at least one ingredient.";
    if (form.ingredients.some((item) => !item.stockId || !item.conversionId || Number(item.quantity) <= 0))
      return "Every ingredient needs an item, unit, and quantity above zero.";
    if (form.ingredients.some((item) => !isRecipeIngredientQuantityAllowed(item, item.quantity)))
      return "Ingredient quantity cannot contain decimal or fraction for the selected unit.";
    if (new Set(form.ingredients.map((item) => item.stockId)).size !== form.ingredients.length)
      return "The same ingredient can only be added once.";
    if (form.quantityType === "Other" && Number(form.quantity) <= 0) return "Enter the custom serving quantity.";
    return "";
  };

  const save = async () => {
    if (form.id ? !canEdit : !canCreate) {
      setNotice({ text: "You do not have permission to save this receipe.", error: true });
      return;
    }
    const validation = validate();
    if (validation) { setNotice({ text: validation, error: true }); return; }
    setSaving(true);
    try {
      const currentIds = form.ingredients.map((item) => item.stockId);
      const payload = {
        apiToken: getAuthSession().apiToken, shopHash: shop.id, stockId: form.stockId,
        receipyById: form.receipyById, quantityType: form.quantityType,
        quantity: Number(form.quantity),
        listAddedItem: form.ingredients.map((item) => ({
          stockId: item.stockId,
          conversionId: item.conversionId,
          quantity: Number(item.quantity),
          wastagePercent: Number(item.wastagePercent || 0),
          optional: item.optional,
          notes: item.notes,
        })),
        listDeletedStocks: form.originalIngredientIds.filter((id) => !currentIds.includes(id)),
        preparationSteps: form.preparationSteps.filter((step) => step.title || step.instruction).map((step, index) => ({
          ...step, stepNo: index + 1, durationMinutes: Number(step.durationMinutes || 0),
        })),
        preparationNotes: form.preparationNotes,
        preparationTimeMinutes: Number(form.preparationTimeMinutes || 0),
      };
      const response = form.id
        ? await snpAxios.put(`${api}receipe/${form.id}`, { jcJson: payload })
        : await snpAxios.post(`${api}receipe`, { jcJson: payload });
      if (response.data.status !== SUCCESS) throw new Error(response.data.errorMessage || "Receipe was not saved.");
      setOpen(false);
      setNotice({ text: form.id ? "Receipe updated." : "Receipe added.", error: false });
      await loadReceipes();
    } catch (error) {
      setNotice({ text: messageOf(error, "Receipe was not saved."), error: true });
    } finally { setSaving(false); }
  };

  const remove = async (item) => {
    if (!canEdit) { setNotice({ text: "Ask the owner for permission to update a receipe.", error: true }); return; }
    if (!window.confirm(`Remove the receipe for ${item.stockName}?`)) return;
    setSaving(true);
    try {
      const response = await snpAxios.delete(`${api}receipe/${item.id}`, { params });
      if (response.data.status !== SUCCESS) throw new Error("Receipe was not removed.");
      setNotice({ text: "Receipe removed.", error: false });
      await loadReceipes();
    } catch (error) { setNotice({ text: messageOf(error, "Receipe was not removed."), error: true }); }
    finally { setSaving(false); }
  };

  const ingredientValue = (index, ingredient) => {
    if (!ingredient.stockId) return null;
    return (ingredientOptions[index] || []).find((option) => option.id === ingredient.stockId)
      || { id: ingredient.stockId, ...ingredient };
  };

  const dishValue = form.stockId
    ? dishOptions.find((option) => option.id === form.stockId)
      || { id: form.stockId, stockName: form.stockName }
    : null;

  const selectDish = (option) => {
    if (!option) {
      setForm((current) => ({ ...current, stockId: "", stockName: "" }));
      setDishSearchTerm("");
      return;
    }
    const stockName = getRecipeIngredientLabel(option);
    setForm((current) => ({ ...current, stockId: option.id, stockName }));
    setDishOptions([option]);
    setDishSearchTerm(stockName);
  };

  const selectIngredient = (index, option) => {
    if (!option) {
      setIngredientValues(index, {
        stockId: "", conversionId: "", stockName: "", unitName: "",
        unitRestrictions: false, decimalSmRestrictions: false,
      });
      setIngredientSearchTerms((current) => ({ ...current, [index]: "" }));
      return;
    }
    const selected = toRecipeIngredient(option);
    setIngredientValues(index, selected);
    setIngredientOptions((current) => ({ ...current, [index]: [option] }));
    setIngredientSearchTerms((current) => ({ ...current, [index]: selected.stockName }));
  };

  const filtered = receipes.filter((item) => `${item.stockName} ${item.receipeBy}`.toLowerCase().includes(search.toLowerCase()));

  if (!canView) return <div className="receipe-v2"><div className="receipe-v2__notice is-error">You are not allowed to view Receipe Management. Contact the owner.</div></div>;

  return <div className="receipe-v2">
    {(loading || saving) && <Loader />}
    <header className="receipe-v2__hero">
      <div><span className="receipe-v2__kicker">Kitchen preparation</span><h1>Receipe Management</h1>
        <p>Keep every dish consistent with ingredients, portions, and clear cooking steps.</p></div>
      {canCreate && <button className="receipe-v2__primary" onClick={openNew}>+ Add receipe</button>}
    </header>
    {notice.text && <div className={`receipe-v2__notice ${notice.error ? "is-error" : ""}`}>
      <span>{notice.text}</span><button onClick={() => setNotice({ text: "", error: false })}>×</button>
    </div>}
    <div className="receipe-v2__tools">
      <label><span>Find a dish</span><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by dish or cook" /></label>
      <div className="receipe-v2__count"><strong>{filtered.length}</strong><span>active receipes</span></div>
    </div>
    <main className="receipe-v2__grid">
      {filtered.map((item) => <article className="receipe-card" key={item.id}>
        <div className="receipe-card__top"><div className="receipe-card__dish">{item.stockName?.charAt(0) || "R"}</div>
          <div><span>Dish receipe</span><h2>{item.stockName || "Unnamed dish"}</h2></div></div>
        <div className="receipe-card__facts">
          <div><strong>{item.ingredients?.length || item.itemSize || 0}</strong><span>Ingredients</span></div>
          <div><strong>{item.preparationTimeMinutes || 0}</strong><span>Minutes</span></div>
          <div><strong>{item.quantityType || "Full"}</strong><span>Serving</span></div>
        </div>
        <p className="receipe-card__cook">Prepared by <strong>{item.receipeBy || "Kitchen team"}</strong></p>
        <div className="receipe-card__actions"><button onClick={() => openEdit(item.id)}>{canEdit ? "View & edit" : "View"}</button>{canEdit && <button className="danger" onClick={() => remove(item)}>Remove</button>}</div>
      </article>)}
      {!loading && !filtered.length && <div className="receipe-v2__empty"><span>🍲</span><h2>No receipes found</h2><p>Add your first dish receipe or change the search.</p></div>}
    </main>

    {open && <div className="receipe-modal" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setOpen(false)}>
      <section className="receipe-modal__panel" role="dialog" aria-modal="true" aria-label="Receipe editor">
        <header><div><span>{form.id ? "Update kitchen guide" : "Create kitchen guide"}</span><h2>{form.id ? "Edit receipe" : "New receipe"}</h2></div><button onClick={() => setOpen(false)} aria-label="Close">×</button></header>
        <div className="receipe-modal__body">
          <section className="receipe-section"><div className="receipe-section__heading"><span>1</span><div><h3>Dish and serving</h3><p>Choose the menu dish and who owns the preparation.</p></div></div>
            <div className="receipe-fields">
              <label><span>Menu dish *</span><Autocomplete
                options={dishOptions}
                value={dishValue}
                inputValue={dishSearchTerm}
                disabled={Boolean(form.id)}
                loading={dishLoading}
                filterOptions={(options) => options}
                getOptionLabel={getRecipeIngredientLabel}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onInputChange={(event, value, reason) => {
                  setDishSearchTerm(value);
                  if (reason === "input") searchDishes(value);
                }}
                onChange={(event, option) => selectDish(option)}
                noOptionsText={dishSearchTerm.trim().length < 2
                  ? "Type at least 2 characters" : "No menu dish found"}
                slotProps={{ popper: { style: { zIndex: 1600 } } }}
                renderInput={(params) => <TextField {...params} size="small"
                  placeholder="Search menu dish by name" />}
              /></label>
              <label><span>Prepared by</span><select value={form.receipyById} disabled={form.id ? !canEdit : !canCreate} onChange={(e) => setForm({ ...form, receipyById: e.target.value })}><option value="">Not specified</option>{employees.map((employee) => <option key={employee.id} value={employee.id}>{employee.details}</option>)}</select></label>
              <label><span>Serving size</span><select value={form.quantityType} onChange={(e) => setForm({ ...form, quantityType: e.target.value })}>{Object.keys(foodQuantityTypes).map((type) => <option key={type}>{type}</option>)}</select></label>
              {form.quantityType === "Other" && <label><span>Custom serving quantity</span><input type="number" min="0.01" step="0.01" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></label>}
              <label><span>Total preparation time</span><div className="receipe-input-suffix"><input type="number" min="0" value={form.preparationTimeMinutes} onChange={(e) => setForm({ ...form, preparationTimeMinutes: e.target.value })} /><em>min</em></div></label>
            </div>
          </section>

          <section className="receipe-section"><div className="receipe-section__heading"><span>2</span><div><h3>Ingredients</h3><p>Search a stock-enabled RateCard and enter quantity in its displayed unit.</p></div></div>
            <div className="receipe-repeat">
              {form.ingredients.map((ingredient, index) => <div className="ingredient-row" key={`${index}-${ingredient.stockId}`}>
                <label className="wide"><span>Ingredient *</span><Autocomplete
                  options={ingredientOptions[index] || []}
                  value={ingredientValue(index, ingredient)}
                  inputValue={ingredientSearchTerms[index] ?? ingredient.stockName ?? ""}
                  loading={Boolean(ingredientLoading[index])}
                  filterOptions={(options) => options}
                  getOptionLabel={getRecipeIngredientLabel}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onInputChange={(event, value, reason) => {
                    setIngredientSearchTerms((current) => ({ ...current, [index]: value }));
                    if (reason === "input") searchIngredients(index, value);
                  }}
                  onChange={(event, option) => selectIngredient(index, option)}
                  noOptionsText={(ingredientSearchTerms[index] || "").trim().length < 2
                    ? "Type at least 2 characters" : "No stock-enabled RateCard found"}
                  slotProps={{ popper: { style: { zIndex: 1600 } } }}
                  renderInput={(params) => <TextField {...params} size="small"
                    placeholder="Search ingredient by name" />}
                /></label>
                <label><span>Unit *</span><input value={ingredient.unitName || ""} readOnly
                  placeholder="Select an ingredient" /></label>
                <label><span>Quantity *</span><input type="number"
                  {...getRecipeIngredientQuantityInput(ingredient)} value={ingredient.quantity}
                  onChange={(e) => setIngredient(index, "quantity", e.target.value)} /></label>
                <label><span>Wastage %</span><input type="number" min="0" max="100" value={ingredient.wastagePercent} onChange={(e) => setIngredient(index, "wastagePercent", e.target.value)} /></label>
                <label className="wide"><span>Kitchen note</span><input value={ingredient.notes} onChange={(e) => setIngredient(index, "notes", e.target.value)} placeholder="Washed, finely chopped…" /></label>
                <label className="receipe-check"><input type="checkbox" checked={ingredient.optional} onChange={(e) => setIngredient(index, "optional", e.target.checked)} /><span>Optional</span></label>
                <button className="receipe-icon-danger" disabled={form.ingredients.length === 1} onClick={() => removeIngredient(index)} aria-label="Remove ingredient">×</button>
              </div>)}
            </div>
            <button className="receipe-add-row" onClick={() => setForm((current) => ({ ...current, ingredients: [...current.ingredients, emptyIngredient()] }))}>+ Add another ingredient</button>
          </section>

          <section className="receipe-section"><div className="receipe-section__heading"><span>3</span><div><h3>Preparation method</h3><p>Write short steps that are easy to follow during service.</p></div></div>
            <div className="receipe-repeat">
              {form.preparationSteps.map((step, index) => <div className="step-row" key={index}><b>{index + 1}</b>
                <label><span>Step name</span><input value={step.title} onChange={(e) => setStep(index, "title", e.target.value)} placeholder="Prepare the base" /></label>
                <label className="wide"><span>Instruction</span><textarea rows="2" value={step.instruction} onChange={(e) => setStep(index, "instruction", e.target.value)} placeholder="Describe what the cook should do…" /></label>
                <label><span>Time</span><div className="receipe-input-suffix"><input type="number" min="0" value={step.durationMinutes} onChange={(e) => setStep(index, "durationMinutes", e.target.value)} /><em>min</em></div></label>
                <button className="receipe-icon-danger" onClick={() => removeStep(index)} aria-label="Remove step">×</button>
              </div>)}
            </div>
            <button className="receipe-add-row" onClick={() => setForm((current) => ({ ...current, preparationSteps: [...current.preparationSteps, emptyStep(current.preparationSteps.length + 1)] }))}>+ Add preparation step</button>
            <label className="receipe-notes"><span>Chef notes</span><textarea rows="3" value={form.preparationNotes} onChange={(e) => setForm({ ...form, preparationNotes: e.target.value })} placeholder="Plating, holding, allergy, or service notes…" /></label>
          </section>
        </div>
        <footer><button className="receipe-v2__secondary" onClick={() => setOpen(false)}>Close</button>{(!form.id ? canCreate : canEdit) && <button className="receipe-v2__primary" disabled={saving} onClick={save}>{saving ? "Saving…" : form.id ? "Save changes" : "Add receipe"}</button>}</footer>
      </section>
    </div>}
  </div>;
}
